// 📊 Vue d'ensemble V1 — dialogue « CA total signé » : recherche client, vue par mois ET par client, ajouts au mois d'acceptation.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/config';
import { API_URL } from '@/lib/apiUrl';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-CA') : '—');

export function CASigneDialog({ annee, onFermer, deptView = 'global' }) {
  const [q, setQ] = useState('');
  const [vue, setVue] = useState('mois');
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/finance/ca-signe/detail`, { params: { annee, q } })
        .then((r) => { setData(r.data); setErreur(''); }).catch(() => setErreur('Impossible de charger le détail du CA signé.'));
    }, 250);
    return () => clearTimeout(t);
  }, [annee, q]);
  const filtreDept = (l) => deptView === 'global' || l.departement === deptView;
  const parMois = (data?.par_mois || []).map((m) => ({ ...m, lignes: m.lignes.filter(filtreDept) })).map((m) => ({ ...m, total: m.lignes.reduce((s, l) => s + l.montant_ht, 0) }));
  const parClient = (data?.par_client || []).filter(filtreDept);
  const total = parMois.reduce((s, m) => s + m.total, 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onFermer} data-testid="dialog-ca-signe">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-4 space-y-3 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-bold text-lg">CA total signé {annee}{deptView !== 'global' ? ` — ${deptView}` : ''}</h2>
              <p className="text-xs text-slate-500">Contrats au mois de signature ; ajouts au mois d'acceptation (approbation, sinon signature). Un ajout n'est jamais un nouveau contrat.</p>
            </div>
            <button onClick={onFermer} className="p-1 hover:bg-slate-100 rounded" data-testid="ca-signe-fermer"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client ou un dossier…" className="w-full border rounded-lg pl-8 pr-2 py-2 text-sm" data-testid="ca-signe-recherche" />
            </div>
            <button onClick={() => setVue('mois')} className={`px-3 py-1.5 rounded-lg text-sm ${vue === 'mois' ? 'bg-slate-900 text-white' : 'bg-slate-100'}`} data-testid="ca-signe-vue-mois">Par mois</button>
            <button onClick={() => setVue('client')} className={`px-3 py-1.5 rounded-lg text-sm ${vue === 'client' ? 'bg-slate-900 text-white' : 'bg-slate-100'}`} data-testid="ca-signe-vue-client">Par client</button>
            <span className="text-sm font-bold ml-auto" data-testid="ca-signe-total">Total : {formatCurrency(total)} · {data?.nb_contrats ?? 0} contrat(s)</span>
          </div>
        </div>
        <div className="p-4 text-sm">
          {erreur && <p className="text-red-600" data-testid="ca-signe-erreur">{erreur}</p>}
          {data && vue === 'mois' && (
            <table className="w-full" data-testid="ca-signe-table-mois">
              <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1">Mois</th><th className="text-right">Contrats</th><th className="text-right">Ajouts</th><th className="text-right">Total</th><th className="text-right">Nb contrats</th></tr></thead>
              <tbody>
                {parMois.filter((m) => m.lignes.length).map((m) => (
                  <MoisLigne key={m.mois} m={m} />
                ))}
                {!parMois.some((m) => m.lignes.length) && <tr><td colSpan={5} className="py-4 text-center text-slate-500" data-testid="ca-signe-vide">Aucun contrat ni ajout pour cette recherche.</td></tr>}
              </tbody>
            </table>
          )}
          {data && vue === 'client' && (
            <div className="space-y-3" data-testid="ca-signe-liste-clients">
              {parClient.map((c) => (
                <div key={c.dossier_id} className="border rounded-lg p-3" data-testid={`ca-signe-client-${c.dossier_id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <a href={c.lien} className="font-semibold text-blue-700 hover:underline" data-testid={`ca-signe-client-${c.dossier_id}-lien`}>{c.client}</a>
                      <span className="text-xs text-slate-500 ml-2">{c.numero_dossier} · {c.departement}</span>
                    </div>
                    <b data-testid={`ca-signe-client-${c.dossier_id}-total`}>Total signé : {formatCurrency(c.total_client)}</b>
                  </div>
                  <ul className="mt-2 space-y-1 text-xs">
                    {c.contrat ? <li className="flex justify-between"><span>Contrat initial — signé le {fmtDate(c.contrat.date)}</span><span>{formatCurrency(c.contrat.montant_ht)}</span></li>
                      : <li className="text-slate-500">Contrat signé une autre année</li>}
                    {c.ajouts.map((a) => (
                      <li key={a.ajout_id} className="flex justify-between" data-testid={`ca-signe-ajout-${a.ajout_id}`}>
                        <span><Badge className="bg-amber-100 text-amber-800 mr-1">Ajout</Badge>{a.description} — accepté le {fmtDate(a.date)}</span><span>{formatCurrency(a.montant_ht)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {!parClient.length && <p className="text-slate-500 text-center py-4" data-testid="ca-signe-vide">Aucun client pour cette recherche.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MoisLigne({ m }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <>
      <tr className="border-b cursor-pointer hover:bg-slate-50" onClick={() => setOuvert((o) => !o)} data-testid={`ca-signe-mois-${m.mois}`}>
        <td className="py-2 font-medium">{m.label}</td><td className="text-right">{formatCurrency(m.contrats)}</td><td className="text-right text-amber-700">{formatCurrency(m.ajouts)}</td>
        <td className="text-right font-bold">{formatCurrency(m.total)}</td><td className="text-right">{m.nb_contrats}</td>
      </tr>
      {ouvert && m.lignes.map((l, i) => (
        <tr key={`${l.dossier_id}-${l.ajout_id || i}`} className="text-xs bg-slate-50" data-testid={`ca-signe-ligne-${l.type}-${l.ajout_id || l.dossier_id}`}>
          <td className="py-1 pl-4" colSpan={3}>{l.type === 'ajout' && <Badge className="bg-amber-100 text-amber-800 mr-1">Ajout</Badge>}<a href={`/crm/dossiers/${l.dossier_id}`} className="text-blue-700">{l.client}</a> · {l.description} · {fmtDate(l.date)}</td>
          <td className="text-right">{formatCurrency(l.montant_ht)}</td><td />
        </tr>
      ))}
    </>
  );
}
